import React, { useState, useEffect, useRef, useContext } from "react";
import { SignUpContainer } from "../SignUpInvitedUser/style";
import { TwoFAComp } from "./style";
import Form from "../../Form/Form";
import PageTitle from "../../PageTitle/PageTitle";
import { BrandContext } from "../../../App";

import { Alert } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import Modal from "../../../components/Modal/Modal";
import Input from "../../../components/Form/Input/Input";

import DefaultButton from "../../../components/simple/DefaultButton";

import { useAPI } from "/src/hooks/useAPI";

import LinearProgress from "@mui/material/LinearProgress";

import { useAuth } from "../../../contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";

const MobileNumberWatcher = ({ watch, setMobileNumber }) => {
  const watchedMobile = watch("mobile_number");
  useEffect(() => {
    if (watchedMobile) {
      setMobileNumber(watchedMobile);
    }
  }, [watchedMobile, setMobileNumber]);
  return null;
};

const TwoFA = ({ isSetup = true }: { isSetup?: boolean }) => {
  const { brand, partialAuthToken } = useContext(BrandContext);

  const { saveAuthToken, authToken, fetchUser, loggedInUser } = useAuth();
  const queryClient = useQueryClient();

  const [codeSentTo, setCodeSentTo] = useState(
    localStorage.getItem("2fa_code_sent_to") ||
      localStorage.getItem("2fa_mobile_number_masked") ||
      "",
  );
  const [deliveryMethod, setDeliveryMethod] = useState(
    localStorage.getItem("2fa_delivery_method") || "sms",
  );
  const [emailBackupAvailable, setEmailBackupAvailable] = useState(
    localStorage.getItem("2fa_email_backup_available") === "1",
  );
  // After SMS setup is confirmed, backend requires email backup verification before finalising
  const [emailVerificationStep, setEmailVerificationStep] = useState(false);
  const [emailVerificationDest, setEmailVerificationDest] = useState("");
  const [twoFAError, setTwoFAError] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [mobileNumberModalOpen, setMobileNumberModalOpen] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]); // Local state for UI
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const formRef = useRef(null);
  const navigate = useNavigate();
  const { token } = useParams();
  const haveSubmitted = useRef(false);

  const { callAPI } = useAPI();

  const [countdown, setCountdown] = useState(0); // Countdown state
  const timeRemainingRef = useRef(600); // 10 minutes in seconds

  const clearTwoFAStorage = () => {
    localStorage.removeItem("2fa_code_sent_to");
    localStorage.removeItem("2fa_mobile_number_masked");
    localStorage.removeItem("2fa_delivery_method");
    localStorage.removeItem("2fa_already_exists_on_account");
    localStorage.removeItem("2fa_email_backup_available");
    sessionStorage.removeItem("2fa_started_at")
  };

  const authTokenRef = useRef(authToken);
  useEffect(() => {
    authTokenRef.current = authToken;
  }, [authToken]);

  const handleExpiredRedirect = (errorMessage) => {
    if (
      errorMessage === "Error: Authentication required. Please login first."
    ) {
      clearTwoFAStorage();
      saveAuthToken("");
      setTimeout(() => {
        window.location.href = "/login";
      }, 1500);
    }
  };

  useEffect(() => {
    if (loggedInUser && loggedInUser.is_2fa_enforced == false) {
      // If user is logged in and 2FA is not mandatory, redirect to dashboard
      navigate("/dashboard/home");
    }
  }, [loggedInUser]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    // Anchor to when the 2FA flow started, not component mount
    const EXPIRATION_MS = 15 * 60 * 1000;
    const startKey = "2fa_started_at";

    if (!sessionStorage.getItem(startKey)) {
      sessionStorage.setItem(startKey, String(Date.now()));
    }

    const expiresAt = Number(sessionStorage.getItem(startKey)) + EXPIRATION_MS;

    const expirationTimer = setInterval(() => {
      if (Date.now() >= expiresAt) {
        clearInterval(expirationTimer);
        clearTwoFAStorage();
        sessionStorage.removeItem(startKey);

        if (authTokenRef.current.length > 0) {
          saveAuthToken("");
          setTimeout(() => {
            window.location.href = "/login?session=2fa-expired";
          }, 500);
        } else {
          window.location.href = "/login?session=2fa-expired";
        }
      }
    }, 1000);

    return () => clearInterval(expirationTimer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle input change
  const handleChange = (
    index: number,
    value: string,
    setValue: (name: string, value: string) => void,
  ) => {
    // Check if this is a full 6-digit code being autofilled into the first input
    if (index === 0 && value.length === 6 && /^\d{6}$/.test(value)) {
      console.log("Full 6-digit code detected in first input:", value);
      const newCode = value.split("");
      setCode(newCode);
      setValue("verification_code", value);

      // Distribute to all inputs visually
      newCode.forEach((digit, i) => {
        if (inputRefs.current[i]) {
          inputRefs.current[i]!.value = digit;
        }
      });

      handleSubmitForm(); // Trigger form submission
      return;
    }

    // Allow clearing the input (empty string)
    if (value === "") {
      const newCode = [...code];
      newCode[index] = "";
      setCode(newCode);
      setValue("verification_code", newCode.join(""));
      return;
    }

    // Only allow single digits for manual entry
    if (/^[0-9]$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode); // Update local UI state
      setValue("verification_code", newCode.join("")); // Update form state
      console.log("Code updated:", newCode.join("")); // Debug log

      // Move to next input if value entered
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }

      // Auto-submit when all fields filled
      if (newCode.every((digit) => digit !== "") && index === 5) {
        console.log("All fields filled, triggering submit"); // Debug log
        handleSubmitForm();
      }
    }
  };

  // Handle paste
  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    index: number,
    setValue: (name: string, value: string) => void,
  ) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData)
      .getData("text")
      .trim();
    const pasted = text.replace(/\D/g, "");

    if (!pasted) return;

    const newCode = [...code];

    for (let i = 0; i < pasted.length && index + i < 6; i++) {
      newCode[index + i] = pasted[i];
      // ✅ Add this — update the DOM input directly
      if (inputRefs.current[index + i]) {
        inputRefs.current[index + i]!.value = pasted[i];
      }
    }

    setCode(newCode);
    setValue("verification_code", newCode.join(""));

    const nextFocus = Math.min(index + pasted.length, 5);
    inputRefs.current[nextFocus]?.focus();

    if (newCode.every(Boolean) && newCode.length === 6) {
      handleSubmitForm();
    }
  };

  // Handle key navigation
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    const target = e.currentTarget;

    if (e.key === "Backspace") {
      if (!target.value && index > 0) {
        e.preventDefault();
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle resend code
  const handleResend = async () => {
    setCountdown(60);

    // Might need a different endpoint here as no access to
    // mobile number
    await callAPI("2fa/resend_token", {
      method: "POST",
      body: {
        partial_auth_token: token,
      },
      token: token ? null : authToken,
    })
      .then(() => {
        setSuccess("Code has been resent");
        setError("");
        setCode(["", "", "", "", "", ""]); // Reset local UI state
        inputRefs.current[0]?.focus();
      })
      .catch((err) => {
        // console.log(
        //   "2FA setup required and automatic setup failed: " + err.message
        // );
        // setErrorMessage(err);

        // Soft error
        // User will be prompted to setup 2fa when logging in
        //handleExpiredRedirect(err);

        if (
          err?.message === "Error: Authentication required. Please login first."
        ) {
          clearTwoFAStorage();
          saveAuthToken("");
          setTimeout(() => {
            window.location.href = "/login";
          }, 1500);
        } else {
          setSuccess("");
          setError(err?.message);
          setCountdown(0); // Reset countdown on error
        }
      });
  };

  const handleSubmitForm = (fake = false) => {
    if (formRef.current && !haveSubmitted.current) {
      haveSubmitted.current = true;
      if (fake) {
        setTimeout(() => {
          haveSubmitted.current = false;
        }, 2000);
      } else {
        setTimeout(() => formRef.current.submit(), 100);
      }
    }
  };
  // Enhanced autofill handler for Safari, Chrome, and Firefox mobile
  useEffect(() => {
    const handleAutofill = () => {
      // Small delay to allow browser to populate the input
      setTimeout(() => {
        const firstInput = inputRefs.current[0];
        if (!firstInput) return;

        const firstValue = firstInput.value;

        // Check if first input contains full 6-digit code (Chrome/Firefox mobile behavior)
        if (firstValue.length === 6 && /^\d{6}$/.test(firstValue)) {
          console.log("Full code detected in first input:", firstValue);
          const newCode = firstValue.split("");
          setCode(newCode);

          // Distribute to all inputs visually
          newCode.forEach((digit, i) => {
            if (inputRefs.current[i]) {
              inputRefs.current[i]!.value = digit;
            }
          });

          // Trigger submit
          if (formRef.current) {
            console.log("Autofilled code, triggering submit");
            handleSubmitForm();
          }
          return;
        }

        // Collect values from all inputs (Safari behavior)
        const values = inputRefs.current.map((input) => input?.value || "");
        const fullCode = values.join("").replace(/\D/g, "");

        console.log(
          "Autofill detected, values:",
          values,
          "fullCode:",
          fullCode,
        );

        if (fullCode.length === 6) {
          const newCode = fullCode.split("");
          setCode(newCode);

          // Trigger submit
          handleSubmitForm();
        } else if (fullCode.length > 0) {
          // Partial autofill - distribute what we have
          const newCode = [...code];
          for (let i = 0; i < fullCode.length && i < 6; i++) {
            newCode[i] = fullCode[i];
          }
          setCode(newCode);
        }
      }, 100);
    };

    // Listen to both input and change events for better autofill support
    const inputs = inputRefs.current;
    inputs.forEach((input) => {
      if (input) {
        input.addEventListener("input", handleAutofill);
        input.addEventListener("change", handleAutofill);
      }
    });

    return () => {
      inputs.forEach((input) => {
        if (input) {
          input.removeEventListener("input", handleAutofill);
          input.removeEventListener("change", handleAutofill);
        }
      });
    };
  }, [code]);

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
    setTimeout(() => {
      // if setup is true and masked number is not in local storage call /enable to begin 2fa setup
      if (isSetup && !codeSentTo) {
        callAPI("2fa/enable/status", {
          method: "POST",
          body: { partial_auth_token: token },
          token: null,
        })
          .then((response) => {
            const dest =
              response["code_sent_to"] || response["mobile_number_masked"];
            if (dest) {
              localStorage.setItem("2fa_code_sent_to", dest);
              setCodeSentTo(dest);
            }
            if (response["delivery_method"]) {
              localStorage.setItem(
                "2fa_delivery_method",
                response["delivery_method"],
              );
              setDeliveryMethod(response["delivery_method"]);
            }
          })
          .catch((err) => {
            console.error("Error fetching 2FA setup status:", err);
          });
      }
    }, 1000);
  }, []);

  return (
    <SignUpContainer>
      <TwoFAComp className="twofa-container" hasError={!!error}>
        <PageTitle
          text={emailVerificationStep ? "Verify Email Backup" : "Enter Code"}
          icon={
            <svg
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17.4999 2.33333V3.96666C17.4999 4.62006 17.4999 4.94675 17.3728 5.19632C17.2609 5.41584 17.0824 5.59432 16.8629 5.70617C16.6133 5.83333 16.2866 5.83333 15.6333 5.83333H12.3666C11.7132 5.83333 11.3865 5.83333 11.1369 5.70617C10.9174 5.59432 10.7389 5.41584 10.6271 5.19632C10.4999 4.94675 10.4999 4.62006 10.4999 3.96666V2.33333M9.56658 25.6667H18.4333C19.74 25.6667 20.3934 25.6667 20.8926 25.4123C21.3316 25.1886 21.6886 24.8317 21.9123 24.3926C22.1666 23.8935 22.1666 23.2401 22.1666 21.9333V6.06666C22.1666 4.75987 22.1666 4.10648 21.9123 3.60735C21.6886 3.16831 21.3316 2.81135 20.8926 2.58765C20.3934 2.33333 19.74 2.33333 18.4333 2.33333H9.56659C8.2598 2.33333 7.6064 2.33333 7.10727 2.58765C6.66823 2.81135 6.31127 3.16831 6.08757 3.60735C5.83325 4.10648 5.83325 4.75987 5.83325 6.06666V21.9333C5.83325 23.2401 5.83325 23.8935 6.08757 24.3926C6.31127 24.8317 6.66823 25.1886 7.10727 25.4123C7.6064 25.6667 8.2598 25.6667 9.56658 25.6667Z"
                stroke="#030039"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
        />
        <p>
          {emailVerificationStep
            ? `We've sent a backup verification code to ${emailVerificationDest || "your email"}`
            : deliveryMethod === "email" || codeSentTo?.includes("@")
              ? `We've sent a verification code to ${codeSentTo}`
              : `We've sent a verification code to your phone number ending in ${codeSentTo || "your device"}`}
        </p>
        <Form
          ref={formRef}
          action={
            emailVerificationStep
              ? "2fa/verify_email_backup"
              : isSetup
                ? "2fa/verify_setup"
                : token
                  ? "login/verify_2fa"
                  : "2fa/verify"
          }
          auth={token == null}
          onSubmit={() => {
            handleSubmitForm(true);
          }}
          onSuccess={(data) => {
            setError("");
            setSuccess("");

            // After SMS confirmed, backend requires email backup verification
            if (data.requires_email_verification) {
              setEmailVerificationStep(true);
              setEmailVerificationDest(data.code_sent_to || "");
              setCode(["", "", "", "", "", ""]);
              inputRefs.current.forEach((input) => {
                // ← add this
                if (input) input.value = ""; // ← clears the DOM
              });
              haveSubmitted.current = false;
              setTimeout(() => inputRefs.current[0]?.focus(), 100);
              return;
            }

            if (data.token) {
              saveAuthToken(data.token);
            }
            clearTwoFAStorage();
            console.log("Submission successful:", data); // Debug log
            queryClient.invalidateQueries({ queryKey: ["user"] });
            fetchUser();
            // Check if there is a redirect stored
            const redirectPath = sessionStorage.getItem("post_2fa_redirect");
            if (
              redirectPath &&
              !redirectPath.includes("2fa") &&
              !redirectPath.includes("login")
            ) {
              sessionStorage.removeItem("post_2fa_redirect");
              setTimeout(() => {
                window.location.href = redirectPath;
              }, 100);
              return;
            } else {
              window.location.href = "/dashboard/home";
            }
          }}
          onError={(error) => {
            setError(error);
            //handleExpiredRedirect(error);
            haveSubmitted.current = false;
            console.error("Submission error:", error); // Debug log
          }}
        >
          {({ register, setValue, formState: { errors } }) => {
            setValue("partial_auth_token", token);
            return (
              <>
                <div className="input-container">
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      type="tel"
                      maxLength={index === 0 ? 6 : 1} // Allow first input to receive full code
                      onChange={(e) =>
                        handleChange(index, e.target.value, setValue)
                      }
                      onPaste={(e) => handlePaste(e, index, setValue)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      ref={(el) => (inputRefs.current[index] = el)}
                      autoComplete={index === 0 ? "one-time-code" : "off"}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="0"
                      disabled={haveSubmitted.current}
                    />
                  ))}
                </div>
                {error && (
                  <>
                    <Alert severity="error" sx={{ mb: 10, mt: 6 }}>
                      {error}
                    </Alert>
                  </>
                )}
                {success && (
                  <>
                    <Alert severity="success" sx={{ mb: 10, mt: 6 }}>
                      {success}
                    </Alert>
                  </>
                )}
              </>
            );
          }}
        </Form>
        {haveSubmitted.current && (
          <LinearProgress
            id="login-loading"
            color="secondary"
            sx={{ mt: 5, height: "8px", borderRadius: "6px", mb: 5 }}
          />
        )}
        <div className="resend">
          <p>Didn't receive a code?</p>
          <button
            className="resend-code"
            onClick={handleResend}
            disabled={countdown > 0}
            style={{
              opacity: countdown > 0 ? 0.5 : 1,
              cursor: countdown > 0 ? "not-allowed" : "pointer",
            }}
          >
            {countdown > 0 ? `Resend code (${countdown}s)` : "Resend code"}
          </button>
        </div>
        {!emailVerificationStep &&
          !isSetup &&
          emailBackupAvailable &&
          deliveryMethod === "sms" && (
            <div className="resend" style={{ marginTop: "8px" }}>
              <p>Can't access your phone?</p>
              <button
                className="resend-code"
                onClick={async () => {
                  try {
                    const res = await callAPI("2fa/use_email_backup", {
                      method: "POST",
                      body: { partial_auth_token: token },
                      token: token ? null : authToken,
                    });
                    setCodeSentTo(res.code_sent_to || "");
                    setDeliveryMethod("email");
                    setEmailBackupAvailable(false);
                    setSuccess("Verification code sent to your backup email.");
                    setError("");
                    setCode(["", "", "", "", "", ""]);
                    inputRefs.current[0]?.focus();
                  } catch (err) {
                    setError(
                      err?.message || "Failed to send backup email code.",
                    );
                  }
                }}
              >
                Send code to my email instead
              </button>
            </div>
          )}
        <div className="helpers">
          <p>Having trouble signing in?</p>
          {localStorage.getItem("2fa_already_exists_on_account") ? (
            <button onClick={() => setMobileNumberModalOpen(true)}>
              Change Device
            </button>
          ) : (
            <button
              onClick={() =>
                (window.location.href = "mailto:support@gurunow.ai")
              }
            >
              Contact Support
            </button>
          )}
        </div>
      </TwoFAComp>
      <Modal
        id="twofa-prompt"
        title="Change Mobile Number"
        open={mobileNumberModalOpen}
        onClose={() => {
          setMobileNumberModalOpen(false);
          setError("");
        }}
        showIcon
        showClose={false}
        slim
        icon={
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17 10V8C17 5.23858 14.7614 3 12 3C9.23858 3 7 5.23858 7 8V10M12 14.5V16.5M8.8 21H15.2C16.8802 21 17.7202 21 18.362 20.673C18.9265 20.3854 19.3854 19.9265 19.673 19.362C20 18.7202 20 17.8802 20 16.2V14.8C20 13.1198 20 12.2798 19.673 11.638C19.3854 11.0735 18.9265 10.6146 18.362 10.327C17.7202 10 16.8802 10 15.2 10H8.8C7.11984 10 6.27976 10 5.63803 10.327C5.07354 10.6146 4.6146 11.0735 4.32698 11.638C4 12.2798 4 13.1198 4 14.8V16.2C4 17.8802 4 18.7202 4.32698 19.362C4.6146 19.9265 5.07354 20.3854 5.63803 20.673C6.27976 21 7.11984 21 8.8 21Z"
              stroke="#01ECFF"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        }
      >
        <p style={{ margin: "0 0 15px 0", fontSize: "14px", fontWeight: 400 }}>
          Enter the new mobile number you'd like to use for verification. We'll
          send a new 6-digit code to this number.
        </p>

        <Form
          action={`2fa/enable`}
          method="POST"
          onSuccess={(data) => {
            const dest = data["code_sent_to"] || data["mobile_number_masked"];
            if (dest) {
              localStorage.setItem("2fa_code_sent_to", dest);
              setCodeSentTo(dest);
            }
            if (data["delivery_method"]) {
              localStorage.setItem(
                "2fa_delivery_method",
                data["delivery_method"],
              );
              setDeliveryMethod(data["delivery_method"]);
            }
            navigate(`/2fa_verify/${token}`);
            location.reload();
          }}
          onError={(err) => {
            setTwoFAError(err);
          }}
        >
          {({ register, formState: { errors }, watch, control }) => {
            return (
              <>
                <MobileNumberWatcher
                  watch={watch}
                  setMobileNumber={setMobileNumber}
                />
                <input
                  type="hidden"
                  name="partial_auth_token"
                  value={token}
                  {...register("partial_auth_token")}
                />
                <Input
                  type="mobile"
                  label="Mobile Number"
                  id="mobile_number"
                  error={errors?.mobile_number?.message}
                  register={register("mobile_number", {
                    required: "Mobile number is required",
                  })}
                  control={control}
                />
                <DefaultButton type="submit" className="action-button">
                  Verify
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4.1665 10H15.8332M15.8332 10L9.99984 4.16669M15.8332 10L9.99984 15.8334"
                      stroke="#030039"
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </DefaultButton>
              </>
            );
          }}
        </Form>
        {twoFAError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {twoFAError}
          </Alert>
        )}
      </Modal>
    </SignUpContainer>
  );
};

export default TwoFA;
